import { TelegramWebApp } from "@/types/telegram";

/**
 * Access to the Telegram WebApp object, and the handful of behaviours that
 * differ when the site is running as a Mini App.
 *
 * Every function here is safe to call on the ordinary web: `webApp()` is null
 * outside Telegram and each wrapper falls back to what a browser would do. That
 * is what lets the rest of the app stay unaware of Telegram - only this file
 * and context/TelegramProvider know about it.
 */

export const webApp = (): TelegramWebApp | null => window.Telegram?.WebApp ?? null;

/*
 * The Mini App SDK. It has to come from telegram.org (Telegram only injects WebApp for a page
 * that loaded this exact script), and it sets a telegram.org cookie, so it loads only when
 * Telegram opened the page.
 */
const SDK_URL = "https://telegram.org/js/telegram-web-app.js?57";

// Telegram puts its launch params in the hash; the SDK keeps them in sessionStorage for a
// reload, and the phone apps inject a native bridge.
const openedByTelegram = (): boolean => {
  if (window.location.hash.includes("tgWebApp")) return true;
  if ("TelegramWebviewProxy" in window) return true;
  try {
    return sessionStorage.getItem("__telegram__initParams") !== null;
  } catch {
    return false;
  }
};

/** Settles once the SDK has loaded, failed, or was not needed; the first render waits for it. */
export const telegramSdkReady: Promise<void> = openedByTelegram()
  ? new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = SDK_URL;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.appendChild(script);
    })
  : Promise.resolve();

/**
 * True when the page really is inside Telegram.
 *
 * `window.Telegram.WebApp` exists as soon as the SDK script loads, even in a
 * normal browser tab, so its presence proves nothing. A non-empty `initData` is
 * the signal: Telegram only fills it in for a page it opened itself. It is also
 * exactly what the backend needs to log anyone in, so "is a Mini App" and "can
 * sign in from Telegram" are the same question.
 */
export const isTelegramMiniApp = (): boolean => Boolean(webApp()?.initData);

export const telegramInitData = (): string => webApp()?.initData ?? "";

/** The `startapp=` payload of the launch link, which is how referral codes arrive. */
export const telegramStartParam = (): string | undefined =>
  webApp()?.initDataUnsafe?.start_param || undefined;

export const telegramPlatform = (): string | undefined => webApp()?.platform;

/**
 * Telegram Login Widget - the web sign-in, for a player in an ordinary browser
 * rather than inside Telegram.
 *
 * The widget is used in **redirect** mode: Telegram sends the browser back to a
 * URL of ours with the signed fields appended to its query string. The obvious
 * alternative, callback mode, hands the page a JavaScript object instead - and
 * that object's `id` is a JavaScript number, so a 64-bit Telegram id has
 * already been rounded past 2^53 before any of our code can see it. The query
 * string keeps every field as text, which is the whole reason this flow is
 * built the way it is. Nothing here parses the payload: it is forwarded
 * verbatim to the backend, which checks its HMAC.
 *
 * `tg_login=1` is our own marker, telling the page on load that this particular
 * query string is a Telegram redirect rather than a normal visit. The backend's
 * check string is a whitelist of Telegram's six fields, so the marker - and any
 * campaign parameter that happens to be on the URL - is ignored by the
 * signature rather than breaking it.
 */
const WIDGET_MARKER = "tg_login";

/** The fields Telegram appends, which have to be cleaned off the URL afterwards. */
const WIDGET_FIELDS = [
  "id",
  "first_name",
  "last_name",
  "username",
  "photo_url",
  "auth_date",
  "hash",
];

/**
 * Where Telegram should send the player back to: this same page, plus the
 * marker. Keeping the current path and query means a sign-in started from a
 * game or a promotion returns there instead of dumping the player on the lobby.
 */
export const telegramWidgetAuthUrl = (): string => {
  const url = new URL(window.location.href);
  url.searchParams.set(WIDGET_MARKER, "1");
  // A stale payload left in the address bar would otherwise be sent back to
  // Telegram as part of the return URL and land here twice.
  WIDGET_FIELDS.forEach((field) => url.searchParams.delete(field));
  url.hash = "";
  return url.toString();
};

/**
 * The raw query string of a Telegram redirect, or null for an ordinary page
 * load. Both the marker and a `hash` must be present - the marker alone is
 * something a player could have bookmarked.
 */
export const readTelegramWidgetAuth = (): string | null => {
  const search = window.location.search;
  if (!search) return null;

  const params = new URLSearchParams(search);
  if (params.get(WIDGET_MARKER) !== "1" || !params.get("hash")) return null;

  return search.replace(/^\?/, "");
};

/**
 * Takes the Telegram fields back off the address bar.
 *
 * Called whatever the outcome, and promptly: until it runs, the signed payload
 * is in the URL, which means it is in the browser's history, in the `Referer`
 * of anything the page loads next, and in whatever the player might paste to
 * somebody. The backend spends each payload on first use, so the copy left
 * behind is already useless - this keeps it from being there to find at all.
 *
 * `replaceState` rather than a navigation: it rewrites the current history
 * entry, so Back does not return to the URL we are trying to get rid of.
 */
export const clearTelegramWidgetAuth = (): void => {
  const url = new URL(window.location.href);
  [...WIDGET_FIELDS, WIDGET_MARKER].forEach((field) => url.searchParams.delete(field));

  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
};

const atLeast = (version: string): boolean => {
  const app = webApp();
  try {
    return Boolean(app?.isVersionAtLeast?.(version));
  } catch {
    return false;
  }
};

/**
 * Opens a URL outside the Mini App.
 *
 * `window.open` is the problem this exists to solve: inside Telegram's webview
 * a popup either silently does nothing or replaces the Mini App, which is how a
 * player ends up losing the app to a hosted cashier page. `openLink` hands the
 * URL to the Telegram client, which opens it in a browser on top of the app, so
 * the player comes back to where they were.
 *
 * `target` is honoured on the ordinary web; in Telegram it is ignored because
 * the client decides.
 */
export const openExternalUrl = (url: string, target = "_blank"): Window | null => {
  const app = webApp();

  if (app && isTelegramMiniApp()) {
    // t.me links belong to Telegram itself and must not go to a browser.
    if (/^https?:\/\/(t\.me|telegram\.me)\//i.test(url)) {
      app.openTelegramLink(url);
      return null;
    }

    app.openLink(url);
    return null;
  }

  return window.open(url, target);
};

/**
 * A window handle for a flow that opens a blank tab first and fills it in once
 * a URL comes back from the server (the two cashiers do this, so the popup is
 * not blocked by the time the request resolves).
 *
 * In Telegram there is no blank tab to hold open, so this returns null and the
 * caller is expected to fall back to `openExternalUrl` with the final URL.
 */
export const openPendingWindow = (): Window | null =>
  isTelegramMiniApp() ? null : window.open("", "_blank");

export const telegramHaptic = (
  kind: "light" | "medium" | "heavy" | "success" | "error" | "warning",
): void => {
  const app = webApp();
  if (!app || !atLeast("6.1")) return;

  try {
    if (kind === "success" || kind === "error" || kind === "warning") {
      app.HapticFeedback.notificationOccurred(kind);
      return;
    }
    app.HapticFeedback.impactOccurred(kind);
  } catch {
    // Haptics are decoration; an older client that throws must not break a tap.
  }
};

/**
 * Hands Telegram the colour the page is actually painted in, so its header and
 * bottom bar match instead of showing the player's own Telegram theme around a
 * dark casino.
 *
 * The colour is read off the rendered body rather than the MUI theme because
 * the theme is built on CSS variables - `palette.background.default` is the
 * string "var(--template-palette-…)", which Telegram will not accept.
 */
export const syncTelegramChrome = (): void => {
  const app = webApp();
  if (!app) return;

  const rgb = getComputedStyle(document.body).backgroundColor;
  const parts = rgb.match(/\d+(\.\d+)?/g);
  if (!parts || parts.length < 3) return;

  const hex = `#${parts
    .slice(0, 3)
    .map((value) => Math.round(Number(value)).toString(16).padStart(2, "0"))
    .join("")}`;

  try {
    if (atLeast("6.1")) app.setHeaderColor?.(hex);
    if (atLeast("6.1")) app.setBackgroundColor?.(hex);
    if (atLeast("7.10")) app.setBottomBarColor?.(hex);
  } catch {
    // A client that refuses a colour keeps its own; not worth a failure.
  }
};
